import {
  DefaultButton,
  Link,
  Panel,
  PanelType,
  SearchBox,
  Text,
} from '@fluentui/react';
import { useCallback, useEffect, useState } from 'react';
import DataGridUI from '../DataGrid/DataGridUI';
import type { DataGridColumn } from '../DataGrid/DataGridUI';
import { useSearchInput } from '../hooks/useSearchInput';
import type { GetTenantsFn, ITenantLike } from './types';

export interface ITenantSelectorProps {
  isOpen: boolean;
  onDismiss: () => void;
  getTenants: GetTenantsFn;
  onTenantSelected: (tenant: ITenantLike) => void;
  selectedTenantId?: number;
  headerText?: string;
  searchThreshold?: number;
}

const PAGE_SIZE = 10;

const TenantSelector = ({
  isOpen,
  onDismiss,
  getTenants,
  onTenantSelected,
  selectedTenantId,
  headerText = 'Pick a tenant',
  searchThreshold = 1,
}: ITenantSelectorProps) => {
  const [tenants, setTenants] = useState<ITenantLike[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [isLastPage, setIsLastPage] = useState(true);
  const [term, setTerm] = useState('');

  const fetchPage = useCallback(
    (search: string, page: number, append: boolean) => {
      const abortController = new AbortController();
      setLoading(true);
      getTenants(
        abortController,
        PAGE_SIZE,
        page,
        undefined,
        search || undefined
      )
        .then((result) => {
          setTenants((prev) =>
            append ? [...prev, ...result.items] : result.items
          );
          setIsLastPage(result.isLastPage ?? result.items.length < PAGE_SIZE);
          setPageNo(page);
        })
        .catch(() => {
          if (!append) setTenants([]);
        })
        .finally(() => setLoading(false));
    },
    [getTenants]
  );

  const runSearch = useCallback(
    (next: string) => {
      setTerm(next);
      fetchPage(next, 1, false);
    },
    [fetchPage]
  );

  useEffect(() => {
    if (!isOpen) {
      setTerm('');
      setPageNo(1);
      setTenants([]);
      setIsLastPage(true);
      return;
    }
    fetchPage('', 1, false);
  }, [isOpen, fetchPage]);

  const {
    getInputProps,
    value: search,
    reset,
  } = useSearchInput({
    onSearch: runSearch,
    threshold: searchThreshold,
  });
  const inputProps = getInputProps();

  const columns: DataGridColumn<ITenantLike>[] = [
    {
      key: 'id',
      ellipsis: true,
      name: 'Id',
      minWidth: 50,
      renderCell: (item) => {
        const selected = item.id === selectedTenantId;
        return selected ? (
          <Text style={{ color: 'var(--colorNeutralForeground4)' }}>
            {item.id}
          </Text>
        ) : (
          <Link key={item.id} onClick={() => onTenantSelected(item)}>
            {item.id}
          </Link>
        );
      },
    },
    {
      key: 'name',
      ellipsis: true,
      name: 'Tenant name',
      minWidth: 220,
      renderCell: (item) =>
        item.id === selectedTenantId
          ? `${item.name} (Currently selected)`
          : item.name,
    },
  ];

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={onDismiss}
      closeButtonAriaLabel="Close"
      type={PanelType.medium}
      headerText={headerText}
      styles={{ content: { paddingTop: 24 } }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <SearchBox
          placeholder="Search tenants"
          value={search}
          onChange={(_ev, newValue) =>
            inputProps.onChange({
              target: { value: newValue ?? '' },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          onSearch={(newValue: string) =>
            inputProps.onKeyDown({
              key: 'Enter',
              currentTarget: { value: newValue },
            } as unknown as React.KeyboardEvent<HTMLInputElement>)
          }
          onClear={() => {
            reset('');
            runSearch('');
          }}
        />
        {(loading || tenants.length > 0) && (
          <DataGridUI<ITenantLike>
            items={tenants}
            columns={columns}
            loading={loading && pageNo === 1}
            getRowId={(item) => item.id}
          />
        )}
        {!loading && tenants.length === 0 && isOpen && (
          <Text>No tenants found</Text>
        )}
        {loading && pageNo > 1 && <Text>Loading...</Text>}
        {!isLastPage && !loading && (
          <DefaultButton
            text="Load more"
            onClick={() => fetchPage(term, pageNo + 1, true)}
          />
        )}
      </div>
    </Panel>
  );
};

export default TenantSelector;
